import { FC, useEffect, useMemo, type HTMLAttributes, type MouseEvent } from 'react';
import { TreeItem } from '@mui/x-tree-view';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Help as HelpIcon } from '@mui/icons-material';
import { TREE_KEYBOARD_SHORTCUTS, treeShortcutHint } from '@/lib/tree/treeKeyboardShortcuts';
import { TreeNode as TreeNodeType } from '@/types/tree';
import { log } from '@/lib/utils/logger';
import { LoadingNode } from '@/components/tree/LoadingNode';
import { TreeNodeIcon } from '@/components/tree/TreeNodeIcon';
import { useTreeStore } from '@/stores/treeStore';
import { useBackend } from '@/hooks/useBackend';
import { useTree } from '@/hooks/useTree';
import { usePrivileges } from '@/hooks/usePrivileges';
import { getAddMenuActions } from '@/types/tree/actions';
import { useNodeState } from '@/hooks/useNodeState';
import { useDataActions } from '@/hooks/useDataState';

/**
 * Props for the TreeNode component
 *
 * This component renders a single node in the tree structure using MUI's TreeItem.
 * It handles:
 * - Node display (label, icon, styling)
 * - Expand/collapse behavior with MUI TreeView
 * - Add child button (+ icon) based on available actions
 * - Context menu (right-click) functionality
 * - Lazy loading of children when expanded
 */
interface TreeNodeProps {
  /** Node data containing metadata and tree structure information */
  node: TreeNodeType;
  /** Optional callback when a child is added via the + button */
  onAddChild?: (nodeId: string, event?: React.MouseEvent) => Promise<boolean>;
  /** Optional callback for context menu (right-click) */
  onContextMenu?: (event: React.MouseEvent, nodeId: string) => void;
}

/**
 * TreeNode component renders a node in the tree view.
 * Supports lazy loading of children and dynamic node rendering.
 *
 * Features:
 * - Lazy loading of children
 * - Dynamic node rendering
 * - Add child button
 * - Loading states
 * - Error handling
 *
 * @component
 */
export const TreeNode: FC<TreeNodeProps> = ({ node, onAddChild, onContextMenu }) => {
  // Get tree operations from hooks
  const { loadChildren, getTypedData } = useTree();
  const { isAuthorized } = usePrivileges();

  // Check if this node's data has been modified (affects styling)
  const isModified = useTreeStore((state) => state.modifiedData.has(node.dataId));

  // Subscribe to node state for expansion and loading
  const { node: currentNode, isExpanded, isLoaded, expand } = useNodeState(node.nodeId);

  // Convert child nodeIds to actual TreeNode objects for rendering
  // Only recompute when children array changes, not when other currentNode properties change
  const nodeChildren = useMemo(() => {
    const children = currentNode?.children;
    if (!children) return [];
    // Map nodeId strings to actual TreeNode objects from the store
    return children.map((nodeId) => useTreeStore.getState().nodes.get(nodeId)).filter(Boolean);
  }, [currentNode?.children]);

  // Subscribe to data loading states
  const { isLoadingChildren } = useDataActions(node.dataId);

  /**
   * Gets the backend API instance from the store.
   */
  const backend = useBackend();

  /**
   * Loads children when node is expanded and not yet loaded.
   * This effect runs whenever the node's expansion state changes.
   */
  useEffect(() => {
    const loadNodeChildren = async (currentNode: TreeNodeType) => {
      // Only load if:
      // 1. Node is expanded
      // 2. Node is expandable
      // 3. Node hasn't been loaded
      // 4. Not currently loading
      if (isExpanded && currentNode.isExpandable && !isLoaded && !isLoadingChildren) {
        try {
          log.debug('[TreeNode] Loading children for node:', { nodeId: currentNode.nodeId });
          await loadChildren(currentNode.nodeId, backend);
        } catch (error: unknown) {
          log.error('[TreeNode] Error loading children:', { nodeId: currentNode.nodeId, error });
        }
      }
    };
    if (currentNode) {
      loadNodeChildren(currentNode);
    }
  }, [currentNode, isExpanded, isLoaded, isLoadingChildren, loadChildren, backend]);

  if (!currentNode) {
    log.error('[TreeNode] Node not found:', { nodeId: node.nodeId });
    return null;
  }

  /**
   * Handles adding a new child node to the current node.
   * This is triggered by the add button click.
   */
  const handleAddChild = async (event: React.MouseEvent) => {
    if (!onAddChild) {
      log.warn('[TreeNode] No onAddChild callback provided');
      return;
    }

    try {
      log.debug('[TreeNode] Adding child to node:', { nodeId: currentNode.nodeId });
      // Pass the button position to the parent
      const wasAdded = await onAddChild(currentNode.nodeId, event);

      // Only expand the node if something was actually added
      if (wasAdded) {
        expand();
      }
    } catch (error: unknown) {
      log.error('[TreeNode] Error adding child:', { nodeId: currentNode.nodeId, error });
    }
  };

  // Show loading state if node is not yet loaded
  if (!isLoaded && isLoadingChildren) {
    return <LoadingNode />;
  }

  // Use node.icon if available, fallback to HelpIcon
  const IconComponent = currentNode.icon || HelpIcon;

  const shouldShowAddButton = (node: TreeNodeType) => {
    // Use centralized logic from actions.ts to determine if any add menu actions are available
    // This replaces the old canHaveChildren logic with the more sophisticated action system
    const addMenuActions = getAddMenuActions(node.type, node, isAuthorized, getTypedData);
    return addMenuActions.some((a) => a.showInAddMenu && a.isVisible(node, isAuthorized, getTypedData));
  };

  return (
    <TreeItem
      itemId={currentNode.nodeId}
      slotProps={{
        // Lets BaseTreeArea scroll the selected row into the tree panel (e.g. deep links to a shipping).
        root: { 'data-list-tree-item-id': currentNode.nodeId } as HTMLAttributes<HTMLLIElement>,
        // Chevron lives inside the content slot; tell MUI not to select when the click came from the icon.
        content: {
          onClick: (event: MouseEvent & { defaultMuiPrevented?: boolean }) => {
            if ((event.target as HTMLElement).closest('.MuiTreeItem-iconContainer')) {
              event.defaultMuiPrevented = true;
            }
          },
        },
      }}
      label={
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          onContextMenu={(e) => onContextMenu?.(e, currentNode.nodeId)}
        >
          <TreeNodeIcon
            icon={IconComponent}
            iconOverlay={currentNode.iconOverlay}
            iconOverlayTitle={currentNode.iconOverlayTitle}
            iconMuted={currentNode.iconMuted}
          />
          <Typography variant="body2" sx={{ flex: 1 }} fontWeight={isModified ? 'bold' : 'normal'}>
            {currentNode.label}
          </Typography>
          {onAddChild && shouldShowAddButton(currentNode) ? (
            <Tooltip title={`Add${treeShortcutHint(TREE_KEYBOARD_SHORTCUTS.add)}`} placement="right">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddChild(e);
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      }
    >
      {/*
        MUI TreeView expand arrow logic:
        - Shows expand arrow only when TreeItem has children
        - We need to render something to trigger the expand arrow for expandable nodes

        Three cases:
        1. Has real children -> render actual TreeNode components
        2. No children but isExpandable -> render invisible dummy to trigger expand arrow
        3. No children and not expandable -> render nothing (no expand arrow)
      */}
      {
        nodeChildren.length > 0 ? (
          // Case 1: Render actual loaded children
          nodeChildren?.map((child) => {
            if (!child) return null; // Handle undefined children
            return <TreeNode key={child.nodeId} node={child} onAddChild={onAddChild} onContextMenu={onContextMenu} />;
          })
        ) : currentNode.isExpandable ? (
          // Case 2: Render invisible dummy to trigger MUI expand arrow
          <TreeItem
            key={'dummy-' + node.nodeId}
            itemId={'dummy-' + node.nodeId}
            label=""
            sx={{ height: 0, overflow: 'hidden' }}
          />
        ) : null /* Case 3: No children, not expandable -> no expand arrow */
      }
    </TreeItem>
  );
};
